import { Button, ButtonLevel, ButtonSize, Text, TextSize } from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface RemoveImageDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the image to remove.
   */
  carouselImageId: string | null;
  /**
   * Callback when the image is removed.
   */
  onRemove: (id: string) => Promise<void>;
}

function RemoveImageDialog(props: RemoveImageDialogProps) {
  const { isOpen = false, onOpenChange, onRemove, carouselImageId, ...other } = props;

  const [isRemoving, setIsRemoving] = React.useState(false);

  const handleOnRemove = React.useCallback(async () => {
    if (!carouselImageId || isRemoving) return;
    setIsRemoving(true);
    try {
      await onRemove?.(carouselImageId);
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove, carouselImageId, isRemoving]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-0">
          {/* The cross, not the trash: removing from the carousel does not delete the image. */}
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={XCloseIcon}>
            <Dialog.Title>Remove image from carousel</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Text elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              Are you sure you want to remove this image from carousel? This won’t delete the image from your Asset
              Library.
            </Text>
          </div>
          <Dialog.Footer>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={handleClose}
              isDisabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnRemove}
              isLoading={isRemoving}
            >
              Remove
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { RemoveImageDialog };
